import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { nigerianInstitutions } from '../data/institutions';
import { services, SubService } from '../data/services';

interface ProjectFormData {
  institution: string;
  selectedService: string | null;
  selectedSubServices: {
    [key: string]: {
      subService: SubService;
      quantity: number;
    };
  };
  projectTitle: string;
  description: string;
  deadline: string;
  budget: number;
  requirements: string;
  contactPhone: string;
  contactEmail: string;
  files: File[];
}

const PostProject: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [filteredInstitutions, setFilteredInstitutions] = useState<string[]>([]);
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const institutionInputRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<ProjectFormData>({
    institution: '',
    selectedService: null,
    selectedSubServices: {},
    projectTitle: '',
    description: '',
    deadline: '',
    budget: 0,
    requirements: '',
    contactPhone: '',
    contactEmail: '',
    files: []
  });

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Pre-fill contact info from user profile
        setFormData(prev => ({
          ...prev,
          contactEmail: parsedUser.email,
          contactPhone: parsedUser.whatsapp_contact || ''
        }));
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [navigate]);

  // Filter institutions based on search
  useEffect(() => {
    if (institutionSearch.trim() === '') {
      setFilteredInstitutions(nigerianInstitutions.slice(0, 50));
    } else {
      const filtered = nigerianInstitutions.filter(inst =>
        inst.toLowerCase().includes(institutionSearch.toLowerCase())
      ).slice(0, 50);
      setFilteredInstitutions(filtered);
    }
  }, [institutionSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (institutionInputRef.current && 
          !institutionInputRef.current.contains(event.target as Node)) {
        setShowInstitutionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle service selection - shows only selected service's sub-services
  const handleServiceSelect = (serviceId: string) => {
    if (formData.selectedService === serviceId) {
      // If clicking the same service, deselect it
      setFormData(prev => ({
        ...prev,
        selectedService: null,
        selectedSubServices: {},
        budget: 0
      }));
    } else {
      // Select new service and clear previous selections
      setFormData(prev => ({
        ...prev,
        selectedService: serviceId,
        selectedSubServices: {},
        budget: 0
      }));
    }
  };

  // Sub-service selection
  const handleSubServiceSelect = (subService: SubService) => {
    if (!formData.selectedService) return;
    
    const key = `${formData.selectedService}-${subService.id}`;
    setFormData(prev => {
      const selectedSubServices = { ...prev.selectedSubServices };
      
      if (selectedSubServices[key]) {
        delete selectedSubServices[key];
      } else {
        selectedSubServices[key] = {
          subService,
          quantity: 1
        };
      }

      // Calculate total budget
      const budget = Object.values(selectedSubServices).reduce(
        (total, item) => total + (item.subService.price * item.quantity),
        0
      );

      return {
        ...prev,
        selectedSubServices,
        budget
      };
    });
  };

  // Update quantity
  const handleQuantityChange = (key: string, quantity: number) => {
    if (quantity < 1) return;
    
    setFormData(prev => {
      const selectedSubServices = { ...prev.selectedSubServices };
      
      if (selectedSubServices[key]) {
        selectedSubServices[key] = {
          ...selectedSubServices[key],
          quantity
        };

        // Recalculate budget
        const budget = Object.values(selectedSubServices).reduce(
          (total, item) => total + (item.subService.price * item.quantity),
          0
        );

        return {
          ...prev,
          selectedSubServices,
          budget
        };
      }
      return prev;
    });
  };

  // Remove sub-service
  const handleRemoveSubService = (key: string) => {
    setFormData(prev => {
      const selectedSubServices = { ...prev.selectedSubServices };
      delete selectedSubServices[key];

      // Recalculate budget
      const budget = Object.values(selectedSubServices).reduce(
        (total, item) => total + (item.subService.price * item.quantity),
        0
      );

      return {
        ...prev,
        selectedSubServices,
        budget
      };
    });
  };

  // Handle file upload
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => {
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`File ${file.name} is too large. Maximum size is 10MB.`);
          return false;
        }
        return true;
      });

      setFormData(prev => ({
        ...prev,
        files: [...prev.files, ...validFiles]
      }));
    }
  };

  // Remove file
  const handleRemoveFile = (index: number) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setUploadProgress(0);

    try {
      // Validation
      if (!formData.institution) {
        throw new Error('Please select your institution');
      }

      if (Object.keys(formData.selectedSubServices).length === 0) {
        throw new Error('Please select at least one service');
      }

      if (!formData.projectTitle.trim()) {
        throw new Error('Please enter a project title');
      }

      if (!formData.description.trim()) {
        throw new Error('Please enter a project description');
      }

      if (!formData.deadline) {
        throw new Error('Please select a deadline');
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add files
      formData.files.forEach((file) => {
        formDataToSend.append(`files`, file);
      });

      // Add project data as JSON string
      const projectData = {
        institution: formData.institution,
        selectedService: formData.selectedService,
        selectedSubServices: Object.entries(formData.selectedSubServices).map(([key, value]) => ({
          serviceKey: key,
          subServiceId: value.subService.id,
          subServiceName: value.subService.name,
          quantity: value.quantity,
          unitPrice: value.subService.price,
          totalPrice: value.subService.price * value.quantity,
          deliveryTime: value.subService.deliveryTime
        })),
        projectTitle: formData.projectTitle,
        description: formData.description,
        deadline: formData.deadline,
        budget: formData.budget,
        requirements: formData.requirements,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        clientId: user.id,
        clientName: user.name,
        clientEmail: user.email
      };

      formDataToSend.append('projectData', JSON.stringify(projectData));

      // Submit project with files
      const response = await axios.post(
        'http://localhost:5000/api/projects/create',
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data.success) {
        setMessage('Project posted successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error posting project:', error);
      setMessage(error.response?.data?.message || error.message || 'Error posting project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const totalItems = Object.values(formData.selectedSubServices).reduce(
    (total, item) => total + item.quantity,
    0
  );

  const selectedService = services.find(s => s.id === formData.selectedService);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation - keep the same as before */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user.username.toUpperCase()}</span>
                <div className="user-meta">
                  <span className="role-badge role-client">Client</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Project Posting Content */}
      <div className="post-project-container">
        <div className="post-project-content">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Post a New Project</h1>
                <p className="text-gray-600">
                  Fill in the details below to find the right freelancer for your academic project
                </p>
              </div>
              <button 
                type="button"
                onClick={() => navigate('/dashboard')} 
                className="btn btn-secondary"
                title="Back to Dashboard"
              >
                ⬅ Back to Dashboard
              </button>
            </div>
          </div>

          {message && (
            <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'} mb-6`}>
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Institution Selection - UPDATED with floating dropdown */}
            <div className="project-card" id="institution-card">
              <h2 className="card-title mb-6">
                <span className="text-red-500">*</span> Select Your Institution
              </h2>
              <div ref={institutionInputRef} className="institution-search-container">
                <label htmlFor="institution" className="form-label">Institution Name</label>
                <div className={`relative institution-input-container ${showInstitutionDropdown ? 'dropdown-open' : ''}`}>
                  <input
                    type="text"
                    id="institution"
                    value={institutionSearch}
                    onChange={(e) => {
                      setInstitutionSearch(e.target.value);
                      setShowInstitutionDropdown(true);
                      if (e.target.value === '') {
                        setFormData({ ...formData, institution: '' });
                      }
                    }}
                    onFocus={() => setShowInstitutionDropdown(true)}
                    className="form-input institution-search-input"
                    placeholder="Type to search for your institution..."
                    required
                    autoComplete="off"
                  />
                  <div className="search-icon">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  {/* Floating Dropdown */}
                  {showInstitutionDropdown && (
                    <>
                      {/* Optional backdrop for mobile - uncomment if needed */}
                      { <div 
                        className="institution-dropdown-backdrop" 
                        onClick={() => setShowInstitutionDropdown(false)}
                      ></div> }
                      
                      <div className="institution-dropdown">
                        {filteredInstitutions.length === 0 ? (
                          <div className="institution-dropdown-item text-gray-500">
                            {institutionSearch.trim() === '' 
                              ? 'Start typing to search for your institution' 
                              : `No institutions found for "${institutionSearch}"`}
                          </div>
                        ) : (
                          <>
                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 dark:bg-gray-900">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {filteredInstitutions.length} institutions found
                              </div>
                            </div>
                            {filteredInstitutions.map((institution, index) => (
                              <button
                                key={index}
                                type="button"
                                className={`institution-dropdown-item ${
                                  formData.institution === institution ? 'selected' : ''
                                }`}
                                onClick={() => {
                                  setFormData({ ...formData, institution });
                                  setInstitutionSearch(institution);
                                  setShowInstitutionDropdown(false);
                                }}
                                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                              >
                                <div className="flex items-start">
                                  <span className="flex-1 text-left">{institution}</span>
                                  {formData.institution === institution && (
                                    <span className="ml-2 text-primary-600 font-bold">✓</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="form-hint mt-2">
                  Start typing to find your institution. All Nigerian universities, polytechnics, colleges, and technical schools are included.
                </div>
              </div>
            </div>

            {/* Service Selection */}
            <div className="project-card">
              <h2 className="card-title mb-6">
                <span className="text-red-500">*</span> Select Services Needed
              </h2>
              
              {!formData.selectedService ? (
                // Show all services for selection
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className={`service-card cursor-pointer transition-all duration-200 p-6 rounded-lg border-2 ${
                        formData.selectedService === service.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                      }`}
                      onClick={() => handleServiceSelect(service.id)}
                    >
                      <div className="flex flex-col h-full">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.name}</h3>
                              <p className="text-sm text-gray-600">{service.description}</p>
                            </div>
                            <div className={`service-select-indicator ${formData.selectedService === service.id ? 'selected' : ''}`}>
                              {formData.selectedService === service.id ? '✓' : '+'}
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between text-sm text-gray-500">
                            <span>{service.subServices.length} sub-services available</span>
                            <button
                              type="button"
                              className="text-red-600 hover:text-blue-800 font-medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleServiceSelect(service.id);
                              }}
                            >
                              Select Service →
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Show selected service's sub-services
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-6 p-4 bg-blue-50 rounded-lg">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Selected Service</h3>
                      <p className="text-gray-600">{selectedService?.name}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleServiceSelect(formData.selectedService!)}
                      className="btn btn-secondary btn-sm"
                    >
                      Change Service
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {selectedService?.subServices.map((subService) => {
                      const key = `${formData.selectedService}-${subService.id}`;
                      const isSelected = !!formData.selectedSubServices[key];
                      
                      return (
                        <div
                          key={subService.id}
                          className={`sub-service-card ${isSelected ? 'selected' : ''} cursor-pointer`}
                          onClick={() => handleSubServiceSelect(subService)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{subService.name}</h4>
                              <p className="text-sm text-gray-600 mt-1">{subService.description}</p>
                              <div className="mt-2">
                                <span className="text-lg font-bold text-blue-600">
                                  ₦{subService.price.toLocaleString()}
                                </span>
                                <span className="text-sm text-gray-500 ml-2">per item</span>
                              </div>
                              <div className="mt-1 text-sm text-gray-500">
                                📅 Delivery: {subService.deliveryTime}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className={`select-indicator ${isSelected ? 'selected' : ''}`}>
                                {isSelected ? '✓' : '+'}
                              </div>
                            </div>
                          </div>
                          
                          {isSelected && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="flex items-center justify-between">
                                <label className="text-sm text-gray-700">Quantity:</label>
                                <div className="flex items-center space-x-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const currentQty = formData.selectedSubServices[key].quantity;
                                      handleQuantityChange(key, currentQty - 1);
                                    }}
                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                                  >
                                    -
                                  </button>
                                  <span className="w-12 text-center font-medium">
                                    {formData.selectedSubServices[key].quantity}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const currentQty = formData.selectedSubServices[key].quantity;
                                      handleQuantityChange(key, currentQty + 1);
                                    }}
                                    className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                              <div className="mt-2 text-right">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveSubService(key);
                                  }}
                                  className="text-sm text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Selected Services Summary */}
              {Object.keys(formData.selectedSubServices).length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Services</h3>
                  <div className="space-y-3">
                    {Object.entries(formData.selectedSubServices).map(([key, item]) => (
                      <div key={key} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div>
                          <div className="font-medium text-gray-900">{item.subService.name}</div>
                          <div className="text-sm text-gray-600">
                            Quantity: {item.quantity} × ₦{item.subService.price.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-blue-600">
                            ₦{(item.subService.price * item.quantity).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-500">{item.subService.deliveryTime}</div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                      <div className="text-lg font-bold text-gray-900">Total Budget:</div>
                      <div className="text-2xl font-bold text-green-600">
                        ₦{formData.budget.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Project Details */}
            <div className="project-card">
              <h2 className="card-title mb-6">Project Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label htmlFor="projectTitle" className="form-label">
                    <span className="text-red-500">*</span> Project Title
                  </label>
                  <input
                    type="text"
                    id="projectTitle"
                    value={formData.projectTitle}
                    onChange={(e) => setFormData({ ...formData, projectTitle: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Design and Implementation of E-Learning Platform"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="deadline" className="form-label">
                    <span className="text-red-500">*</span> Deadline
                  </label>
                  <input
                    type="date"
                    id="deadline"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="form-input"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                  <div className="form-hint">Select when you need this project completed</div>
                </div>

                <div className="md:col-span-2">
                  <div className="form-group">
                    <label htmlFor="description" className="form-label">
                      <span className="text-red-500">*</span> Project Description
                    </label>
                    <textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="form-input min-h-[120px]"
                      placeholder="Describe your project in detail. Include any specific requirements, guidelines, or reference materials..."
                      required
                      rows={4}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <div className="form-group">
                    <label htmlFor="requirements" className="form-label">Additional Requirements</label>
                    <textarea
                      id="requirements"
                      value={formData.requirements}
                      onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                      className="form-input min-h-[100px]"
                      placeholder="Any special instructions, file formats, or additional requirements..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="md:col-span-2">
                  <div className="form-group">
                    <label className="form-label">Upload Files (Optional)</label>
                    <div className="mt-2">
                      <div className="flex items-center justify-center w-full">
                        <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg className="w-8 h-8 mb-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                            </svg>
                            <p className="mb-2 text-sm text-gray-500">
                              <span className="font-semibold">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-gray-500">PDF, DOC, DOCX, PPT, Images (Max 10MB each)</p>
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            multiple
                            onChange={handleFileUpload}
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                          />
                        </label>
                      </div>
                      
                      {/* Uploaded Files List */}
                      {formData.files.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <h4 className="text-sm font-medium text-gray-700">Uploaded Files ({formData.files.length})</h4>
                          {formData.files.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg">
                                    {file.type.includes('image') ? '🖼️' : 
                                     file.type.includes('pdf') ? '📄' : 
                                     file.type.includes('word') ? '📝' : 
                                     file.type.includes('presentation') ? '📊' : '📎'}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(file.size)} • {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                                  </p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(index)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Remove file"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-red-600 hover:text-blue-800 text-sm font-medium mt-2"
                          >
                            + Add more files
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="form-hint mt-2">
                      Upload reference materials, guidelines, or any supporting documents. Maximum 10MB per file.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="project-card">
              <h2 className="card-title mb-6">Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <label htmlFor="contactEmail" className="form-label">
                    <span className="text-red-500">*</span> Email Address
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactPhone" className="form-label">
                    <span className="text-red-500">*</span> WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="form-input"
                    placeholder="+2348012345678"
                    required
                  />
                  <div className="form-hint">Include country code (e.g., +234 for Nigeria)</div>
                </div>
              </div>
            </div>

            {/* Summary and Submission */}
            <div className="project-card bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="card-title">Order Summary</h2>
                  <p className="text-gray-600">Review your project details before posting</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">₦{formData.budget.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">{totalItems} item{totalItems !== 1 ? 's' : ''} selected</div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Institution:</span>
                  <span className="font-medium">{formData.institution || 'Not selected'}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Services:</span>
                  <span className="font-medium">
                    {Object.keys(formData.selectedSubServices).length} service{Object.keys(formData.selectedSubServices).length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Files:</span>
                  <span className="font-medium">
                    {formData.files.length} file{formData.files.length !== 1 ? 's' : ''} attached
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Deadline:</span>
                  <span className="font-medium">
                    {formData.deadline ? new Date(formData.deadline).toLocaleDateString() : 'Not set'}
                  </span>
                </div>
              </div>

              {/* Upload Progress */}
              {isSubmitting && uploadProgress > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Uploading files...</span>
                    <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="mt-8 pt-6 border-t border-blue-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    By posting this project, you agree to our Terms of Service. Freelancers will review your project and submit proposals.
                  </div>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard')}
                      className="btn btn-secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || Object.keys(formData.selectedSubServices).length === 0}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="loading-spinner mr-2"></div>
                          {uploadProgress > 0 ? 'Uploading...' : 'Posting Project...'}
                        </>
                      ) : (
                        `Post Project (₦${formData.budget.toLocaleString()})`
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PostProject;